import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  OvenPlayerInstance,
  OvenPlayerStateChange,
  OvenPlayerStreamProps,
} from './types';
import { Wrapper } from './OvenPlayerStream.style';
import { leagueActions } from 'app/slice';

const RECONNECT_INTERVAL = 5000;

const OvenPlayerStreamComponent: React.FC<OvenPlayerStreamProps> = ({
  url,
  waterMark = {},
  crop = false,
}) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<OvenPlayerInstance | null>(null);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const destroyPlayer = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.stop?.();
        playerRef.current.remove?.();
      } catch (e) {
        console.warn('Error destroying player:', e);
      }
      playerRef.current = null;
    }
    if (wrapperRef.current) {
      wrapperRef.current.innerHTML = '';
    }
  }, []);

  const loadScript = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (window.OvenPlayer) return resolve();

      const script = document.createElement('script');
      script.src = '/ovenplayer.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load OvenPlayer'));
      document.body.appendChild(script);
    });
  }, []);

  const retryLoad = useCallback(() => {
    clearRetry();
    retryTimerRef.current = setTimeout(loadPlayer, RECONNECT_INTERVAL);
  }, [clearRetry]);

  const loadPlayer = useCallback(async () => {
    if (!url || !wrapperRef.current) return;
    clearRetry();

    try {
      await loadScript();
      destroyPlayer();

      const container = document.createElement('div');
      wrapperRef.current.appendChild(container);

      const player = window.OvenPlayer!.create(container, {
        sources: [{ type: 'webrtc', file: url }],
        autoStart: true,
        mute: true,
        controls: true,
        waterMark,
      });

      playerRef.current = player;

      const handleError = () => {
        if (!isMountedRef.current) return;
        console.warn('Stream disconnected, retrying...');
        retryLoad();
      };

      player.on('error', handleError);
      player.on('disconnected', handleError);
      player.on('ended', handleError);

      player.on('stateChanged', (playerState: OvenPlayerStateChange) => {
        if (playerState.newstate === 'playing')
          dispatch(leagueActions.setIsStreaming(true));
        if (playerState.newstate === 'error')
          dispatch(leagueActions.setIsStreaming(false));
      });
    } catch (err) {
      console.error('Failed to initialize OvenPlayer:', err);
      if (isMountedRef.current) retryLoad();
    }
  }, [url, destroyPlayer, clearRetry]);

  useEffect(() => {
    isMountedRef.current = true;
    loadPlayer();

    return () => {
      isMountedRef.current = false;
      clearRetry();
      destroyPlayer();
    };
  }, [loadPlayer, clearRetry, destroyPlayer]);

  return <Wrapper ref={wrapperRef} crop={crop} />;
};

const OvenPlayerStream = React.memo(
  OvenPlayerStreamComponent,
  (prev, next) => prev.url === next.url,
);

export default OvenPlayerStream;
